/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useEffect, useState, useCallback } from "react";
import User from "../../../assets/img/icons/user3.png";
import { GiNightSleep } from "react-icons/gi";
import { Link, useNavigate, useParams } from "react-router-dom";
import ProfileTab from "./ProfileTab";
import Breadcrumbs from "../../../components/Breadcrumbs";
import config from "../../../config";
import { formatDate } from "../../../validation";
import { capitalizeFirstLetter } from "../../../utils/basicFunctions";
import EditCandidatesModal from "../../../components/Administration/Jobs/CandidatesModal/EditCandidatesModal";
import ImageEditor from "../../../components/ImageEditor";
import ResignationModal from "../../../components/modelpopup/ResignationModal";
import {
  onLoginSuccess,
  onLogoutSuccess,
} from "../Authentication/TrackingHandler";
import api from "../../../api/axios";
import Loader from "../../../components/Loader";
import toast from "react-hot-toast";
import useEmployeeLocation from "../../../hooks/useEmployeeLocation";
import { companyID, getEmployeeLocalDetails } from "../../../auth";
import CompanyCoordinatesError from "../Authentication/CompanyCoordinatesError";
import { getEmployeeTimeZoneDate } from "../../../utils/getEmployeeTimeZoneDate";
import { getFile } from "../../../utils/getFile";
import { useLocation } from "react-router-dom";
import EmployeeQuickLinks from "../../../components/EmployeeQuickLinks";
import "../../../assets/css/main.css";
import { decrypt } from "../../../utils/crypto";
import EmployeePIP from "../HR/PIP/EmployeePIP";
import WorkDateSelector from "./WorkDateSelector";
import EventCountdown from "./EventCountdown";
import PasswordReset from "./PasswordReset";
import DailyWorkStatusModal from "../Employees/DailyWorkStatus";
import {
  markCheckedIn,
  markCheckedOut,
  startWfhTracking,
  stopWfhTracking,
} from "../../../utils/wfhTracking";

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTab = "emp_profile" } = location.state || {};
  let { employee_id } = useParams();
  employee_id = decrypt(employee_id);
  const [basicDetails, setBasicsDetails] = useState({});
  const [addressDetails, setAddressDetails] = useState([]);
  const [qualificationDetails, setQualificationDetails] = useState([]);
  const [accountDetails, seAccountDetails] = useState({});
  const [employmentDetails, setEmploymentDetails] = useState([]);
  const [editTrue, setEditedTrue] = useState(false);
  const [res, setRes] = useState({});
  const [employeeID, setEmployeeID] = useState("");
  const [fieldOne, setFieldOne] = useState(true);
  const [fieldFour, setFieldFour] = useState(false);
  const [isMainModalOpen, setIsMainModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [showDailyWorkStatusModal, setShowDailyWorkStatusModal] =
    useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [wfhWorkDates, setWfhWorkDates] = useState([]);
  const [openWorkRequestModal, setOpenWorkRequestModal] = useState(
    () => () => {},
  );
  const loginEmployeeId = localStorage.getItem("employeeID");
  const work_location = localStorage.getItem("work_location");
  const employee_role = localStorage.getItem("employee_role");
  const company_id = companyID();
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const [profileImage, setProfileImage] = useState({});
  const {
    getEmployeeCurrentPosition,
    showModal,
    locationErrorMessage,
    setShowModal,
  } = useEmployeeLocation();

  const employeeLocalDetails = getEmployeeLocalDetails();

  useEffect(() => {
    if (isEditModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.removeProperty("overflow");
    }
    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [isEditModalOpen]);

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading({ show: true, message: "Loading profile..." });
      const approved = "rejected";
      const response = await api.get(
        `/api/profile?employee_id=${employee_id}&login_status=${approved}`,
      );

      if (!response?.data?.success) {
        throw new Error("Failed to fetch profile data");
      }
      const { data } = response.data;
      setRes(data?.employeeDetails);
      setBasicsDetails(data?.employeeDetails?.basicDetails || {});
      setAddressDetails(data?.employeeDetails?.addresses || []);
      setQualificationDetails(data?.employeeDetails?.qualifications || []);
      setEmploymentDetails(data?.employeeDetails?.employmentDetails?.[0] || {});
      seAccountDetails(data?.employeeDetails?.accountDetails?.[0] || {});

      const todayDate = getEmployeeTimeZoneDate();
      const todayAttendance =
        data?.attendance?.filter((record) => record.Date === todayDate) || [];
      setAttendance(todayAttendance);

      setWfhWorkDates(data?.wfhDates || []);
    } catch (err) {
      console.error("Error fetching profile data:", err);
      toast.error("Failed to load profile. Please refresh.");
    } finally {
      setLoading({ show: false, message: "" });
    }
  }, [employee_id]);

  useEffect(() => {
    fetchProfileData();
  }, [employee_id, editTrue, fetchProfileData]);

  const refreshProfileData = () => {
    setEditedTrue(!editTrue);
  };

  const handleCloseDailyWorkStatusModal = () => {
    setShowDailyWorkStatusModal(false);
  };

  // Resolves today's row from employee_wfh_dates. That table holds two kinds of
  // rows for a hybrid employee:
  //   * the recurring weekly schedule (work_date = NULL, hybrid_days = weekday)
  //   * one-off requests for a specific day (work_date = YYYY-MM-DD)
  // A one-off request for today always wins over the weekly schedule, otherwise
  // an approved WFH on a day the schedule marks Onsite would still be treated
  // as Onsite (the API returns the hybrid rows first, so a single find() over
  // both conditions picks the weekly row and the check-in gets geo-blocked).
  const findTodayWFH = () => {
    const currentDate = getEmployeeTimeZoneDate();
    const todayDay = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
      .format(new Date())
      .toLowerCase();

    const approvedRows = (wfhWorkDates || []).filter(
      (wfh) => wfh?.approved_status === "approved",
    );

    return (
      approvedRows.find((wfh) => wfh.work_date?.split("T")[0] === currentDate) ||
      approvedRows.find((wfh) => wfh.hybrid_days?.toLowerCase() === todayDay) ||
      null
    );
  };

  // Resolves the effective work_location_id for today from employee_wfh_dates
  // (the same approved-WFH data the check-in logic already uses), falling back
  // to the employee's default work_location. Work location mapping:
  //   1 = Office, 2 = Work From Home (WFH), 4 = Hybrid
  const getTodayWorkLocationId = () => {
    const matchedWFH = findTodayWFH();
    if (matchedWFH) return Number(matchedWFH.work_location_id);
    return Number(work_location);
  };

  // Today is WFH when the effective work_location_id is 2.
  const computeIsWFH = () => getTodayWorkLocationId() === 2;

  // Forces the browser geolocation permission. Resolves true only if we can
  // read the current position (permission granted); false if denied/unavailable.
  const ensureLocationEnabled = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(false);
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });

  // WFH check-ins require location permission so we can track the employee.
  // Returns true if allowed to proceed, false (with a toast) if it must block.
  const ensureWFHLocationPermission = async () => {
    if (!computeIsWFH()) return true;
    const granted = await ensureLocationEnabled();
    if (!granted) {
      toast.error(
        "Location permission is required to check in while working from home. Please enable location access and try again.",
      );
    }
    return granted;
  };

  // Records the check-in state (for the tab-close guard) and, when the check-in
  // is a WFH check-in, starts random-interval location tracking.
  const startTrackingAfterCheckIn = (punchDate) => {
    markCheckedIn({ employeeId: employee_id, punchDate });
    if (computeIsWFH()) {
      startWfhTracking({
        employeeId: employee_id,
        workLocation: work_location,
        punchDate,
      });
    }
  };

  const markAttendance = async () => {
    setLoading({ show: true, message: `Checking current location...` });

    // WFH check-in: require location permission before allowing check-in.
    const wfhAllowed = await ensureWFHLocationPermission();
    if (!wfhAllowed) {
      setLoading({ show: false, message: "" });
      return;
    }

    let inLocation = true;
    if (work_location == 1 || work_location == 4) {
      const matchedWFH = findTodayWFH();
      const isWFHDate = !!matchedWFH;

      if (!isWFHDate) {
        inLocation = await getEmployeeCurrentPosition(company_id);
      } else if (matchedWFH.work_location_id == 1) {
        inLocation = await getEmployeeCurrentPosition(company_id);
      } else {
        inLocation = true;
      }
    }
    if (inLocation || (work_location != 1 && work_location != 4)) {
      try {
        setLoading({ show: true, message: `Marking Attendance...` });
        const punchDate = getEmployeeTimeZoneDate();
        const { data } = await api.post(
          `auth/mark-attendance/${loginEmployeeId}`,
          {
            punchDate: punchDate,
          },
        );
        getEmployeeLiveStatus();
        toast.success(data?.message || "Attendance marked successfully");

        onLoginSuccess(
          employeeLocalDetails?.id,
          employeeLocalDetails?.work_start_time,
          employeeLocalDetails?.work_end_time,
        );
        fetchProfileData();
        startTrackingAfterCheckIn(punchDate);
      } catch (error) {
        toast.error("Something went wrong. Please try again later!");
        console.error(error);
      } finally {
        setLoading({ show: false, message: "" });
      }
    } else {
      setLoading({ show: false, message: "" });
    }
  };

  const handlePunchOut = async (e) => {
    e.preventDefault();
    setLoading({ show: true, message: `Checking current location...` });

    // WFH: require location permission before allowing checkout as well.
    const wfhAllowed = await ensureWFHLocationPermission();
    if (!wfhAllowed) {
      setLoading({ show: false, message: "" });
      return;
    }

    let inLocation = true;
    if (work_location == 1 || work_location == 4) {
      // inLocation = await getEmployeeCurrentPosition(company_id);
      // Today's approved work mode (one-off request wins over hybrid schedule)
      const matchedWFH = findTodayWFH();
      const isWFHDate = !!matchedWFH;
      // If current date is not in wfhWorkDates, check location
      if (!isWFHDate) {
        inLocation = await getEmployeeCurrentPosition(company_id);
      } else if (matchedWFH.work_location_id == 1) {
        inLocation = await getEmployeeCurrentPosition(company_id);
      } else {
        inLocation = true;
      }
    }

    if (inLocation || (work_location != 1 && work_location != 4)) {
      setLoading({ show: false, message: "" });
      setShowDailyWorkStatusModal(true);
    } else {
      setLoading({ show: false, message: "" });
    }
  };

  const completePunchOut = async (e) => {
    e?.preventDefault?.();

    try {
      setLoading({ show: true, message: "Checking Out Attendance..." });
      const punchDate = getEmployeeTimeZoneDate();
      const res = await api.post(`auth/logout`, {
        employeeId: employee_id,
        checkOut: true,
        punchDate: punchDate,
      });

      if (res.data.success === true) {
        setShowDailyWorkStatusModal(false);
        await fetchProfileData();
        getEmployeeLiveStatus();
        onLogoutSuccess();
        markCheckedOut();
        stopWfhTracking({ reason: "manual" });
        toast.success("Check Out successfully.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again later!");
      console.error("Error during logout:", err);
    } finally {
      setLoading({ show: false, message: "" });
    }
  };

  const handlePunchIn = async (e) => {
    e.preventDefault();
    setLoading({ show: true, message: `Checking current location...` });

    // WFH check-in: require location permission before allowing check-in.
    const wfhAllowed = await ensureWFHLocationPermission();
    if (!wfhAllowed) {
      setLoading({ show: false, message: "" });
      return;
    }

    let inLocation = true;

    if (work_location == 1 || work_location == 4) {
      // inLocation = await getEmployeeCurrentPosition(company_id);
      // Today's approved work mode (one-off request wins over hybrid schedule)
      const matchedWFH = findTodayWFH();
      const isWFHDate = !!matchedWFH;
      // If current date is not in wfhWorkDates, check location
      if (!isWFHDate) {
        inLocation = await getEmployeeCurrentPosition(company_id);
      } else if (matchedWFH.work_location_id == 1) {
        inLocation = await getEmployeeCurrentPosition(company_id);
      } else {
        inLocation = true;
      }
    }
    if (inLocation || (work_location != 1 && work_location != 4)) {
      try {
        setLoading({ show: true, message: "Checking In Attendance..." });
        const punchDate = getEmployeeTimeZoneDate();
        await api.post(`api/attendance/punch-in`, {
          employeeId: employee_id,
          checkIn: true,
          punchDate: punchDate,
        });

        getEmployeeLiveStatus();
        toast.success("Check In successfully.");
        await fetchProfileData();
        startTrackingAfterCheckIn(punchDate);
      } catch (err) {
        toast.error("Something went wrong. Please try again later!");
        console.error(err);
      } finally {
        setLoading({ show: false, message: "" });
      }
    } else {
      setLoading({ show: false, message: "" });
    }
  };

  useEffect(() => {
    const fetchImage = async () => {
      let image = null;
      if (basicDetails.photo) {
        const imageUrl = await getFile(
          `${config.IMAGE_URL}${basicDetails.photo}`,
        );
        if (imageUrl) {
          image = imageUrl;
        }
      }
      setProfileImage(image);
    };

    fetchImage();
  }, [basicDetails]);

  const [employeeLiveStatus, setEmployeeLiveStatus] = useState({});

  const getEmployeeLiveStatus = async () => {
    try {
      const response = await api.post(
        `employees/employee-live-status?employee_id=${employee_id}`,
      );
      setEmployeeLiveStatus(response?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getEmployeeLiveStatus();
  }, []);

  // Keep the persisted check-in flag in sync with the server's attendance so
  // the app-wide tab-close guard knows whether to prompt/auto-checkout, even
  // after a fresh login or a page reload. Only tracks the logged-in employee.
  useEffect(() => {
    if (employee_id != loginEmployeeId) return;
    if (!attendance || attendance.length === 0) return;
    const punchDate = getEmployeeTimeZoneDate();
    if (!attendance[0]?.punch_status) {
      // punch_status falsy => currently checked in
      markCheckedIn({ employeeId: employee_id, punchDate });
    } else {
      markCheckedOut();
    }
  }, [attendance]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="Profile"
            title="Dashboard"
            subtitle="Profile"
            modal="#add_indicator"
            name="Add New"
          />
          <div className="card mb-0">
            <div className="card-body">
              <div className="row">
                <div className="col-md-12">
                  <div className="profile-view">
                    <div className="profile-img-wrap">
                      {/* make the container relative so the status indicator can be positioned */}
                      <div
                        className="profile-img"
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsMainModalOpen(true);
                          }}
                        >
                          <img src={profileImage || User} alt="User Image" />
                        </Link>
                        {/* Active status indicator: small green circle overlapping top-right with white border and a centered office icon */}
                        {employeeLiveStatus?.active ? (
                          <span
                            aria-hidden="true"
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              width: 16,
                              height: 16,
                              backgroundColor: "#28a745",
                              border: "2px solid #fff",
                              borderRadius: "50%",
                              boxSizing: "content-box",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 2,
                            }}
                            title="Active"
                          >
                            <i
                              className={`fa-solid ${
                                employeeLiveStatus?.workLocation ===
                                  "Work From Home" ||
                                employeeLiveStatus?.workLocation ===
                                  "Work from home"
                                  ? "fa-house"
                                  : employeeLiveStatus?.workLocation ===
                                      "On-site"
                                    ? "fa-building"
                                    : employeeLiveStatus?.workLocation ===
                                        "On-field"
                                      ? "fa-person-walking"
                                      : "fa-building"
                              }`}
                              style={{
                                color: "#fff",
                                fontSize: 9,
                                lineHeight: 1,
                              }}
                            />
                          </span>
                        ) : (
                          <span
                            aria-hidden="true"
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              width: 16,
                              height: 16,
                              backgroundColor: "#e30000ff",
                              border: "1px solid #fff",
                              borderRadius: "50%",
                              boxSizing: "content-box",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 2,
                            }}
                            title="Inactive"
                          >
                            {/* <GiNightSleep   style={{ color: "#fff", fontSize: 9, lineHeight: 1 }}/> */}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="profile-basic">
                      <div className="row">
                        <div className="col-md-5">
                          <div className="profile-info-left">
                            <h3 className="user-name m-t-0 mb-0 capitalize-each-word">
                              {localStorage.getItem("demo_user_name") ||
                                [
                                  basicDetails?.first_name,
                                  basicDetails?.middle_name,
                                  basicDetails?.last_name,
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                            </h3>
                            <h6 className="text-muted">
                              {employmentDetails?.department_name}
                            </h6>
                            <small className="text-muted">
                              {employmentDetails?.designation_name}
                            </small>
                            <div className="staff-id">
                              Employee ID:{" "}
                              {employmentDetails?.employment_id || "N/A"}
                            </div>

                            <div className="small text-muted">
                              <strong>Joined On :</strong>{" "}
                              {employmentDetails?.joining_date
                                ? formatDate(employmentDetails.joining_date)
                                : "-"}
                            </div>

                            {basicDetails?.employment_end_date && (
                              <div
                                className="small text-muted"
                                style={{ cursor: "help" }}
                                title={
                                  basicDetails?.employment_end_reason
                                    ? `Reason: ${basicDetails.employment_end_reason}`
                                    : ""
                                }
                              >
                                <strong>Employment End :</strong>{" "}
                                {formatDate(basicDetails.employment_end_date)}
                              </div>
                            )}
                            {/* <div style={{display:'flex', gap:'10px'}}> */}
                            {/* <div className="staff-msg">
                              <Link
                                
                                className="btn btn-custom" 
                                to="#"
                                onClick={(e)=>{
                                  e.preventDefault();
                                  setIsEditModalOpen(true);
                                }}
                              >
                                Edit Profile
                              </Link>
                            </div> */}
                            {
                              <div className="staff-msg">
                                <div
                                  className="d-flex flex-wrap align-items-center gap-2 
                                  justify-content-center justify-content-md-start"
                                >
                                  {employee_id == loginEmployeeId &&
                                    (attendance.length != 0 ? (
                                      <Link
                                        className="btn btn-custom"
                                        style={{
                                          background: `${
                                            !attendance[0]?.punch_status
                                              ? "red"
                                              : "green"
                                          }`,
                                          color: "white",
                                        }}
                                        to="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          !attendance[0]?.punch_status
                                            ? handlePunchOut(e)
                                            : handlePunchIn(e);
                                        }}
                                      >
                                        {!attendance[0]?.punch_status
                                          ? "Check Out"
                                          : "Check In"}
                                      </Link>
                                    ) : (
                                      <button
                                        className="btn btn-success"
                                        onClick={markAttendance}
                                      >
                                        <i
                                          className="fa-solid fa-clipboard-user me-1"
                                          title="Mark your today's attendance"
                                        />
                                        Check In
                                      </button>
                                    ))}
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => openWorkRequestModal()}
                                  >
                                    Work Mode
                                  </button>
                                  <WorkDateSelector
                                    employee_id={employee_id}
                                    wfhWorkDates={wfhWorkDates}
                                    isCheckedInToday={attendance.length > 0}
                                    onRefresh={refreshProfileData}
                                    setOpenModalAction={setOpenWorkRequestModal}
                                  />
                                </div>
                              </div>
                            }
                            {/* </div> */}
                          </div>
                        </div>
                        <div className="col-md-7">
                          <ul className="personal-info">
                            <li>
                              <div className="title">Phone:</div>
                              <div className="text">
                                <Link
                                  to={`tel:${
                                    localStorage.getItem("demo_user_phone") ||
                                    basicDetails?.number
                                  }`}
                                >
                                  {localStorage.getItem("demo_user_phone") ||
                                    basicDetails?.number}
                                </Link>
                              </div>
                            </li>
                            <li>
                              <div className="title">Official Email:</div>
                              <div className="text">
                                <Link
                                  to={`mailto:${
                                    localStorage.getItem("demo_user_email") ||
                                    basicDetails?.official_email
                                  }}`}
                                >
                                  {localStorage.getItem("demo_user_email") ||
                                    basicDetails?.official_email}
                                </Link>
                              </div>
                            </li>
                            <li>
                              <div className="title">Birthday:</div>
                              <div className="text">
                                {(basicDetails?.date_of_birth &&
                                  formatDate(basicDetails?.date_of_birth)) ??
                                  "-"}
                                {basicDetails?.date_of_birth && (
                                  <EventCountdown
                                    date={basicDetails?.date_of_birth}
                                    type={"birthdate"}
                                  />
                                )}
                              </div>
                            </li>
                            {/* <li>
                            <div className="title">Address:</div>
                            <div className="text">
                              {capitalizeFirstLetter(
                                addressDetails[0]?.address
                              )}
                            </div>
                          </li> */}
                            <li>
                              <div className="title">Gender:</div>
                              <div className="text">
                                {basicDetails?.gender ?? "-"}
                              </div>
                            </li>
                            <li>
                              <div className="title">Employment Status: </div>
                              <div
                                className="text"
                                style={{ textTransform: "capitalize" }}
                              >
                                {employmentDetails?.employment_status ?? "-"}
                              </div>
                            </li>
                            {/* <li>
                              <div className="title">Reports to:</div>
                              <div className="text">
                                <div className="avatar-box">
                                  <div className="avatar avatar-xs">
                                    <img src={Avatar_16} alt="User Image" />
                                  </div>
                                </div>
                                <Link to="profile">
                                  {userData.supervisor.name}
                                </Link>
                              </div>
                            </li> */}
                            <li>
                              <div className="title">Quick links:</div>
                              <div className="text">
                                <button
                                  className={`btn btn-${
                                    !showLinks ? "primary" : "outline-primary"
                                  } py-0`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setShowLinks(!showLinks);
                                  }}
                                >
                                  {!showLinks ? "Show" : "Hide"}
                                  {/* <i
                                    className={`ms-2 fa-${
                                      showLinks ? "regular" : "solid"
                                    } fa-square-caret-down `}
                                  ></i> */}
                                </button>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="pro-edit">
                      <Link
                        data-bs-target="#edit_employee"
                        data-bs-toggle="modal"
                        className="edit-icon"
                        // to={`../edit-employee/${id}`}
                        onClick={(e) => {
                          setEmployeeID(employee_id);
                        }}
                        to="#"
                      >
                        <i className="fa-solid fa-pencil"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="m-2 mt-0">
              {/* <div className="title fw-bold">Quick links:</div> */}
              {showLinks && (
                <EmployeeQuickLinks
                  employee_id={employee_id}
                  basicDetails={basicDetails}
                />
              )}
            </div>
          </div>

          <div className="card tab-box">
            <div className="row user-tabs">
              <div className="col-lg-12 col-md-12 col-sm-12 line-tabs">
                <ul className="nav nav-tabs nav-tabs-bottom">
                  <li className="nav-item">
                    <Link
                      to="#emp_profile"
                      data-bs-toggle="tab"
                      className={`nav-link ${
                        activeTab === "emp_profile" ? "active" : ""
                      }`}
                    >
                      Profile
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="#emp_resignation"
                      data-bs-toggle="tab"
                      className={`nav-link ${
                        activeTab === "emp_resignation" ? "active" : ""
                      }`}
                    >
                      Separation
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="#org_hierarchy"
                      data-bs-toggle="tab"
                      className={`nav-link ${
                        activeTab === "org_hierarchy" ? "active" : ""
                      }`}
                    >
                      ORG Hierarchy
                    </Link>
                  </li>
                  {employeeLocalDetails?.employment_status == "PIP" &&
                    employeeLocalDetails?.id == employee_id && (
                      <li className="nav-item">
                        <Link
                          to="#pip"
                          data-bs-toggle="tab"
                          className={`nav-link ${
                            activeTab === "pip" ? "active" : ""
                          }`}
                        >
                          PIP
                        </Link>
                      </li>
                    )}
                  {employeeLocalDetails?.id == employee_id && (
                    <li className="nav-item">
                      <Link
                        to="#password_reset"
                        data-bs-toggle="tab"
                        className={`nav-link ${
                          activeTab === "password_reset" ? "active" : ""
                        }`}
                      >
                        Password Reset
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
          {/* Profile Info Tab */}
          <ProfileTab
            basicDetails={basicDetails}
            addressDetails={addressDetails}
            employmentDetails={employmentDetails}
            qualificationDetails={qualificationDetails}
            accountDetails={accountDetails}
            setBasicsDetails={setBasicsDetails}
            seAccountDetails={seAccountDetails}
            setAddressDetails={setAddressDetails}
            setQualificationDetails={setQualificationDetails}
            setEditedTrue={setEditedTrue}
            editTrue={editTrue}
            // setFieldThree={setFieldThree}
            setFieldFour={setFieldFour}
            setFieldOne={setFieldOne}
            setAlreadyApplied={setAlreadyApplied}
            separationModal={modalShow}
            activeTab={activeTab}
            setModalShow={setModalShow}
            alreadyApplied={alreadyApplied}
            employee_id={employee_id}
            loginEmployeeId={loginEmployeeId}
          />
          <PasswordReset activeTab={activeTab} />
          <EditCandidatesModal
            employID={employee_id}
            setEditedTrue={setEditedTrue}
            fieldFour={fieldFour}
            setFieldFour={setFieldFour}
            fieldOne={fieldOne}
            setFieldOne={setFieldOne}
            res={res}
            isEditModalOpen={isEditModalOpen}
            setIsEditModalOpen={setIsEditModalOpen}
          />
          <ResignationModal
            show={modalShow}
            onHide={() => setModalShow(false)}
            employeeId={employee_id}
          />
          <EmployeePIP activeTab={activeTab} />
        </div>
        {isEditModalOpen && <div className="modal-backdrop fade show"></div>}
      </div>
      <ImageEditor
        profileImage={profileImage}
        dummyPhoto={User}
        isMainModalOpen={isMainModalOpen}
        setIsMainModalOpen={setIsMainModalOpen}
        employeeId={employee_id}
        setEditedTrue={setEditedTrue}
        editTrue={editTrue}
      />
      <Loader show={loading.show} loadingMessage={loading.message} />
      <CompanyCoordinatesError
        errorMessage={locationErrorMessage}
        show={showModal}
        handleClose={() => setShowModal(false)}
      />
      <DailyWorkStatusModal
        show={showDailyWorkStatusModal}
        handleShowModal={setShowDailyWorkStatusModal}
        handleClose={handleCloseDailyWorkStatusModal}
        handleLogout={completePunchOut}
      />
    </>
  );
};

export default Profile;
